import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment } from '@material-ui/core';
import { Delete, Edit } from '@material-ui/icons';
import { Button, Panel, Select, TextField } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { SelectOption, getOptions } from '@michelin/select-options-provider';
import { useFormContext } from 'react-hook-form';
import { BillingProfileContextData, BillingProfileTypes, BillingServiceTypes, ContactsWithLimitsEdited } from '../types';
import { getProfileLimits, isContactLimitRelevant } from 'modules/Billing/utils';

interface DialogInterface {
  openDialog: boolean;
  closeDialog: () => void;
  row: ContactsWithLimitsEdited;
  rowIndex: number;
}

export function ActionsCell({
  row,
  rowIndex,
  onEdit,
}: {
  row: ContactsWithLimitsEdited;
  rowIndex: number;
  onEdit: (row: ContactsWithLimitsEdited, index: number) => void;
}) {
  const { getValues, setValue } = useFormContext<BillingProfileContextData>();

  return (
    <div>
      <IconButton
        aria-label="edit"
        style={{ margin: '8px' }}
        onClick={() => {
          onEdit(row, rowIndex);
        }}
        size="small"
      >
        <Edit fontSize="small" color="primary" />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ margin: '8px' }}
        onClick={async () => {
          const currentContacts = getValues().contactsWithLimitsEdited;
          let auxContactsWithLimitsEdited;

          if (row.addedForEdit) {
            auxContactsWithLimitsEdited = currentContacts.filter((contact) => contact.id !== row.id);
          } else {
            const hasEntry = currentContacts.some((x) => x.id === row.id);
            const deletedContact = {
              ...row,
              num_of_tires: 0,
              num_of_wheels: 0,
              tire_dollar_amount: 0,
              mechanical_dollar_amount: 0,
              deleted: true,
            };

            if (hasEntry) {
              auxContactsWithLimitsEdited = currentContacts.map((contact) =>
                contact.id === row.id ? deletedContact : contact,
              );
            } else {
              auxContactsWithLimitsEdited = [...currentContacts, deletedContact];
            }
          }
          setValue(`contactsWithLimitsEdited`, auxContactsWithLimitsEdited, { shouldDirty: true });
        }}
        size="small"
      >
        <Delete fontSize="small" color={'primary'} />
      </IconButton>
    </div>
  );
}

export const DialogEditLimits = (props: DialogInterface) => {
  const { row, openDialog, closeDialog, rowIndex } = props;
  const { getValues, setValue } = useFormContext<BillingProfileContextData>();
  const { t } = useTranslation();
  const tireLimitOptions: Array<SelectOption> = getOptions('contacts_approval_limits_no_of_tires');
  const wheelLimitOptions: Array<SelectOption> = getOptions('contacts_approval_limits_no_of_wheels');
  const [tires, setTires] = useState<number | undefined>(row.num_of_tires || 0);
  const [wheels, setWheels] = useState<number | undefined>(row.num_of_wheels || 0);
  const [tireAmount, setTireAmount] = useState<number | undefined>(row.tire_dollar_amount || 0);
  const [mechanicalAmount, setMechanicalAmount] = useState<number | undefined>(row.mechanical_dollar_amount || 0);

  const { profile } = getValues();
  const profileLimits = getProfileLimits(profile);

  const getErrorText = (value: number | undefined, limit: number, fieldName: string) => {
    if (value !== undefined && value <= limit) {
      return t('Contact cannot be added because the approval limit is lower than the general {{fieldName}} limit ({{limit}})', {
        limit,
        fieldName,
      });
    }
    return '';
  };

  const serviceType = profile?.general_information?.service_type;

  const currentContactValues = {
    ...row,
    num_of_tires: tires,
    num_of_wheels: wheels,
    tire_dollar_amount: tireAmount,
    mechanical_dollar_amount: mechanicalAmount,
  };

  const isVisible = isContactLimitRelevant(profile, currentContactValues);

  const tiresError =
    !isVisible && serviceType === BillingServiceTypes.TIRE_WHEEL ? getErrorText(tires, profileLimits.tires, 'Tires') : '';
  const wheelsError =
    !isVisible && serviceType === BillingServiceTypes.TIRE_WHEEL
      ? getErrorText(wheels, profileLimits.wheels, 'Wheels')
      : '';
  const tireAmountError =
    !isVisible &&
    (serviceType === BillingServiceTypes.MECHANICAL || serviceType === BillingServiceTypes.TIRE_WHEEL) &&
    (profile?.general_information?.profile_type === BillingProfileTypes.onsite ||
      profile?.general_information?.profile_type === BillingProfileTypes.ers_onsite)
      ? getErrorText(tireAmount, profileLimits.tireAmount, 'Dollar Amt')
      : '';
  const mechanicalAmountError =
    !isVisible && serviceType === BillingServiceTypes.MECHANICAL
      ? getErrorText(mechanicalAmount, profileLimits.mechanicalAmount, 'Dollar Amt')
      : '';

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        closeDialog();
      }}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">{`${t('Edit Approval Limits')} - ${row.first_name} ${
        row.last_name
      }`}</DialogTitle>
      <DialogContent dividers>
        <Panel variant="none" style={{ border: 0 }} spacing={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Select
                id={`contactsWithLimitsEdited.${rowIndex}.tires`}
                label={t('Number of Tires')}
                type="string"
                variant="standard"
                value={tires || 0}
                options={tireLimitOptions}
                onChange={(e) => setTires(parseInt(e.target.value as string, 10))}
                fullWidth
                style={{ display: 'flex', flexWrap: 'wrap' }}
                error={!!tiresError}
                helperText={tiresError}
              />
            </Grid>
            <Grid item xs={12}>
              <Select
                id={`contactsWithLimitsEdited.${rowIndex}.wheels`}
                label={t('Number of Wheels')}
                type="string"
                variant="standard"
                value={wheels || 0}
                options={wheelLimitOptions}
                onChange={(e) => setWheels(parseInt(e.target.value as string, 10))}
                fullWidth
                style={{ display: 'flex', flexWrap: 'wrap' }}
                error={!!wheelsError}
                helperText={wheelsError}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id={`contactsWithLimitsEdited.${rowIndex}.tireAmount`}
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                label={t('Dollar Amt (Onsite services only)')}
                type="number"
                variant="standard"
                value={tireAmount}
                onChange={(e) => setTireAmount(parseInt(e.target.value, 10))}
                fullWidth
                style={{ display: 'flex', flexWrap: 'wrap' }}
                error={!!tireAmountError}
                helperText={tireAmountError}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id={`contactsWithLimitsEdited.${rowIndex}.mechanicalAmount`}
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                label={t('Dollar Amt (Mechanical services only)')}
                type="number"
                variant="standard"
                value={mechanicalAmount}
                onChange={(e) => setMechanicalAmount(parseInt(e.target.value, 10))}
                fullWidth
                style={{ display: 'flex', flexWrap: 'wrap' }}
                error={!!mechanicalAmountError}
                helperText={mechanicalAmountError}
              />
            </Grid>
          </Grid>
        </Panel>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            const rowUpdated = {
              ...row,
              num_of_tires: tires,
              num_of_wheels: wheels,
              tire_dollar_amount: Number.isNaN(tireAmount) ? 0 : tireAmount,
              mechanical_dollar_amount: Number.isNaN(mechanicalAmount) ? 0 : mechanicalAmount,
            };
            const exists = getValues().contactsWithLimitsEdited.some((contact) => contact.id === rowUpdated.id);
            let auxContactsWithLimitsEdited;

            if (exists) {
              auxContactsWithLimitsEdited = getValues().contactsWithLimitsEdited.map((contact) =>
                contact.id === rowUpdated.id ? rowUpdated : contact,
              );
            } else {
              auxContactsWithLimitsEdited = [...getValues().contactsWithLimitsEdited, rowUpdated];
            }

            setValue(`contactsWithLimitsEdited`, auxContactsWithLimitsEdited, { shouldDirty: true });
            closeDialog();
          }}
          color="success"
        >
          {t('Update')}
        </Button>
        <Button
          onClick={() => {
            closeDialog();
          }}
          color="danger"
        >
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
